import type { ReactNode } from "react";
import { cva } from "class-variance-authority";
import { cn } from "../../lib/utils";

type SectionCardTone = "default" | "primary" | "muted";

const sectionCardVariants = cva(
  "overflow-hidden rounded-lg border border-border/80 text-card-foreground",
  {
    variants: {
      tone: {
        default: "bg-card",
        primary: "bg-primary/5",
        muted: "bg-muted/40",
      },
      rail: {
        true: "border-l-[3px] border-l-primary/55",
        false: "",
      },
    },
    defaultVariants: {
      tone: "default",
      rail: false,
    },
  }
);

/**
 * Compact workbench section built from the shared Tailwind tokens. This remains
 * distinct from the larger shadcn Card tile, but follows the same compositional
 * contract: callers may adjust layout through className/bodyClassName without
 * adding stylesheet selectors.
 */
export function SectionCard({
  tone = "default",
  rail = false,
  title,
  actions,
  className,
  bodyClassName,
  children,
}: {
  tone?: SectionCardTone;
  rail?: boolean;
  title?: ReactNode;
  actions?: ReactNode;
  className?: string;
  bodyClassName?: string;
  children: ReactNode;
}) {
  return (
    <section
      data-slot="section-card"
      className={cn(sectionCardVariants({ tone, rail }), className)}
    >
      {title || actions ? (
        <header
          data-slot="section-card-header"
          className="flex items-center justify-between gap-2 px-3 pt-2.5 pb-2"
        >
          {title ? (
            <h4 className="m-0 text-[0.8125rem] font-semibold text-foreground">{title}</h4>
          ) : (
            <span />
          )}
          {actions ? (
            <div
              data-slot="section-card-actions"
              className="flex flex-wrap items-center gap-1.5"
            >
              {actions}
            </div>
          ) : null}
        </header>
      ) : null}
      <div
        data-slot="section-card-content"
        className={cn("p-3 text-sm leading-relaxed", (title || actions) && "pt-0", bodyClassName)}
      >
        {children}
      </div>
    </section>
  );
}
